import { useId, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { ProductType, SkuRule } from '@nucleus-commerce/api-client/admin';
import {
  SkuCaseTransform,
  SkuMissingCodePolicy,
  SkuNumberScope,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  optionTokens,
  ROOT_TOKENS,
  SKU_RULE_PRESETS,
  VARIANT_TOKENS,
} from '../api/sku-rule-mappers';
import { useAttributes } from '../hooks/use-attributes';
import { useProductTypes } from '../hooks/use-product-types';
import { useSkuRules } from '../hooks/use-sku-rules';
import {
  skuRuleFormSchema,
  type SkuRuleFormInput,
  type SkuRuleFormValues,
} from '../schemas/sku-rule-form-schema';

import { SkuRulePreviewPanel } from './sku-rule-preview-panel';
import { SkuTemplateField } from './sku-template-field';

export const SKU_RULE_FORM_ID = 'sku-rule-form';

export interface SkuRuleFormProps {
  /** The saved rule; null while creating. */
  rule: SkuRule | null;
  defaultValues: SkuRuleFormInput;
  onSubmit: (values: SkuRuleFormValues) => Promise<unknown>;
  isSubmitting: boolean;
  /** Apply-to-existing and the danger zone, only for a saved rule. */
  aside?: ReactNode;
}

const CASE_TRANSFORMS = Object.values(SkuCaseTransform);
const NUMBER_SCOPES = Object.values(SkuNumberScope);
const MISSING_CODE_POLICIES = Object.values(SkuMissingCodePolicy);

const derivedTypeCode = (type: ProductType) => type.slug.slice(0, 2).toUpperCase();

/**
 * The SKU rule editor: presets, name, the two templates with token chips, options and the
 * product type picker in the main column; the live preview, Save with the default toggle, the
 * counters and whatever the caller adds (apply, delete) in the rail. Registers with the save bar.
 */
export function SkuRuleForm({
  rule,
  defaultValues,
  onSubmit,
  isSubmitting,
  aside,
}: SkuRuleFormProps) {
  const { t } = useTranslation(['skuRules', 'common']);
  const message = useErrorMessage();
  const { can } = usePermissions();
  const readOnly = !can('attributes.write');
  const attributes = useAttributes();
  const productTypes = useProductTypes();
  const rules = useSkuRules();
  const groupId = useId();
  const form = useForm<SkuRuleFormInput, unknown, SkuRuleFormValues>({
    resolver: useI18nResolver(skuRuleFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const { isDirty } = form.formState;
  const rootManual = form.watch('rootManual');
  const numberScope = form.watch('numberScope');
  const types = productTypes.data ?? [];

  /** Which other rule claims a type; the rule being edited never counts. */
  const claimedBy = new Map<string, SkuRule>();
  for (const other of rules.data ?? []) {
    if (other.id === rule?.id) continue;
    for (const claimed of other.productTypes) claimedBy.set(claimed.id, other);
  }

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      form.reset(values);
    } catch (error) {
      if (isApiError(error) && error.code === 'PRODUCT_TYPE_CLAIMED') {
        const owner = (rules.data ?? []).find((r) => r.id === error.meta.ruleId);
        const type = types.find((candidate) => candidate.id === error.meta.productTypeId);
        form.setError('productTypeIds', {
          type: 'server',
          message:
            owner && type
              ? t('skuRules:errors.typeClaimed', { type: type.name, rule: owner.name })
              : owner
                ? t('skuRules:errors.typeClaimedByRule', { rule: owner.name })
                : t('skuRules:errors.typeClaimedUnknown'),
        });
      } else if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting, disabled: readOnly });

  const applyPreset = (preset: (typeof SKU_RULE_PRESETS)[number]) => {
    form.setValue('rootManual', preset.rootTemplate === null, { shouldDirty: true });
    if (preset.rootTemplate !== null) {
      form.setValue('rootTemplate', preset.rootTemplate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    form.setValue('variantTemplate', preset.variantTemplate, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.clearErrors(['rootTemplate', 'variantTemplate']);
  };

  const rootError = form.formState.errors.root?.server;

  return (
    <Form {...form}>
      <FormLayout
        asideWidth="wide"
        aside={
          <>
            <FormSection
              title={t('skuRules:sections.save')}
              description={t('skuRules:sections.saveDescription')}
            >
              <FormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <FormItem className="flex items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <FormLabel>{t('skuRules:fields.isDefault')}</FormLabel>
                      <FormDescription>{t('skuRules:fields.isDefaultHint')}</FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={readOnly}
                        form={SKU_RULE_FORM_ID}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Can permission="attributes.write">
                <Button
                  type="submit"
                  form={SKU_RULE_FORM_ID}
                  disabled={isSubmitting || !isDirty}
                  className="w-full"
                >
                  {isSubmitting ? t('common:actions.saving') : t('common:actions.save')}
                </Button>
              </Can>
            </FormSection>
            <SkuRulePreviewPanel ruleId={rule?.id ?? null} productTypes={types} />
            {rule && rule.counters.length > 0 && !rootManual ? (
              <FormSection
                title={t('skuRules:sections.counters')}
                description={t('skuRules:sections.countersDescription')}
              >
                {rule.counters.map((counter, index) => {
                  const name =
                    counter.scopeKey === ''
                      ? t('skuRules:counters.rule')
                      : (types.find((type) => type.id === counter.scopeKey)?.name ??
                        counter.scopeKey);
                  return (
                    <FormField
                      key={counter.scopeKey}
                      control={form.control}
                      name={`counters.${index}.nextNumber`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{name}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min={counter.nextNumber}
                              step={1}
                              form={SKU_RULE_FORM_ID}
                              disabled={readOnly}
                              aria-label={t('skuRules:counters.moveForward', { name })}
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            {t('skuRules:counters.current', { number: counter.nextNumber })}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  );
                })}
              </FormSection>
            ) : null}
            {aside}
          </>
        }
      >
        <form id={SKU_RULE_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertTitle>{t('skuRules:saveFailed')}</AlertTitle>
              <AlertDescription>{rootError.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            title={t('skuRules:sections.details')}
            description={t('skuRules:sections.detailsDescription')}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('skuRules:fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoFocus={rule === null} disabled={readOnly} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection
            title={t('skuRules:sections.templates')}
            description={t('skuRules:sections.templatesDescription')}
          >
            {readOnly ? null : (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">{t('skuRules:presets.title')}</p>
                <div
                  role="group"
                  aria-label={t('skuRules:presets.title')}
                  className="flex flex-wrap gap-2"
                >
                  {SKU_RULE_PRESETS.map((preset) => (
                    <Button
                      key={preset.key}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-auto flex-col items-start gap-0.5 py-1.5"
                      onClick={() => applyPreset(preset)}
                    >
                      <span>{t(`skuRules:presets.${preset.key}`)}</span>
                      <span className="text-muted-foreground font-mono text-[11px] font-normal">
                        {t(`skuRules:presets.${preset.key}Hint`)}
                      </span>
                    </Button>
                  ))}
                </div>
                <p className="text-muted-foreground text-xs">{t('skuRules:presets.description')}</p>
              </div>
            )}
            <FormField
              control={form.control}
              name="rootManual"
              render={({ field }) => (
                <FormItem className="flex items-start justify-between gap-3 rounded-md border p-3">
                  <div className="flex flex-col gap-1">
                    <FormLabel>{t('skuRules:fields.rootManual')}</FormLabel>
                    <FormDescription>{t('skuRules:fields.rootManualHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      disabled={readOnly}
                      onCheckedChange={(next) => {
                        field.onChange(next);
                        if (next) form.clearErrors('rootTemplate');
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {rootManual ? null : (
              <SkuTemplateField
                name="rootTemplate"
                label={t('skuRules:fields.rootTemplate')}
                hint={t('skuRules:fields.rootTemplateHint')}
                tokens={ROOT_TOKENS}
                disabled={readOnly}
              />
            )}
            <SkuTemplateField
              name="variantTemplate"
              label={t('skuRules:fields.variantTemplate')}
              hint={t('skuRules:fields.variantTemplateHint')}
              tokens={[...VARIANT_TOKENS, ...optionTokens(attributes.data ?? [])]}
              disabled={readOnly}
            />
          </FormSection>
          <FormSection
            title={t('skuRules:sections.options')}
            description={t('skuRules:sections.optionsDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="caseTransform"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('skuRules:fields.caseTransform')}</FormLabel>
                    <Select
                      value={field.value}
                      disabled={readOnly}
                      onValueChange={(value) => {
                        const picked = CASE_TRANSFORMS.find((c) => c === value);
                        if (picked) field.onChange(picked);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CASE_TRANSFORMS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`skuRules:caseTransform.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="numberPadding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('skuRules:fields.numberPadding')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={8}
                        disabled={readOnly || rootManual}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('skuRules:fields.numberPaddingHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="numberScope"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('skuRules:fields.numberScope')}</FormLabel>
                    <Select
                      value={field.value}
                      disabled={readOnly || rootManual}
                      onValueChange={(value) => {
                        const picked = NUMBER_SCOPES.find((s) => s === value);
                        if (picked) field.onChange(picked);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {NUMBER_SCOPES.map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`skuRules:numberScope.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="missingCodePolicy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('skuRules:fields.missingCodePolicy')}</FormLabel>
                    <Select
                      value={field.value}
                      disabled={readOnly}
                      onValueChange={(value) => {
                        const picked = MISSING_CODE_POLICIES.find((p) => p === value);
                        if (picked) field.onChange(picked);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {MISSING_CODE_POLICIES.map((option) => (
                          <SelectItem key={option} value={option}>
                            {t(`skuRules:missingCodePolicy.${option}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxLength"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('skuRules:fields.maxLength')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={8}
                        max={64}
                        disabled={readOnly}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('skuRules:fields.maxLengthHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <FormSection
            title={t('skuRules:sections.productTypes')}
            description={t('skuRules:sections.productTypesDescription')}
          >
            <FormField
              control={form.control}
              name="productTypeIds"
              render={({ field }) => {
                const chosen = new Set(field.value);
                const toggle = (id: string, on: boolean) => {
                  const next = types.filter((type) => (type.id === id ? on : chosen.has(type.id)));
                  field.onChange(next.map((type) => type.id));
                };
                return (
                  <FormItem>
                    {types.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        {t('skuRules:productTypes.none')}
                      </p>
                    ) : (
                      <ul
                        aria-label={t('skuRules:productTypes.list')}
                        className="grid gap-2 sm:grid-cols-2"
                      >
                        {types.map((type) => {
                          const id = `${groupId}-${type.id}`;
                          const owner = claimedBy.get(type.id);
                          const perTypeCounter =
                            numberScope === 'product_type' && chosen.has(type.id);
                          return (
                            <li
                              key={type.id}
                              className="flex items-start gap-2 rounded-md border p-3"
                            >
                              <Checkbox
                                id={id}
                                className="mt-0.5"
                                checked={chosen.has(type.id)}
                                disabled={readOnly || owner !== undefined}
                                aria-describedby={`${id}-meta`}
                                onCheckedChange={(next) => toggle(type.id, next === true)}
                              />
                              <div className="flex min-w-0 flex-col gap-0.5">
                                <Label htmlFor={id} className="font-normal">
                                  {type.name}
                                </Label>
                                <span id={`${id}-meta`} className="text-muted-foreground text-xs">
                                  {owner
                                    ? t('skuRules:productTypes.claimedBy', { rule: owner.name })
                                    : type.skuCode
                                      ? t('skuRules:productTypes.code', { code: type.skuCode })
                                      : t('skuRules:productTypes.noCode', {
                                          derived: derivedTypeCode(type),
                                        })}
                                  {perTypeCounter ? null : null}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </FormSection>
        </form>
      </FormLayout>
    </Form>
  );
}
